import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Unlink } from 'lucide-react';

import { Button } from '@/components';
import { cn } from '@/lib/utils';

import { NumberInput } from './NumberInput';

interface PerSideControlProps {
  top: number | undefined;
  right: number | undefined;
  bottom: number | undefined;
  left: number | undefined;
  onChange: (side: 'top' | 'right' | 'bottom' | 'left', value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  labels?: { top?: string; right?: string; bottom?: string; left?: string };
}

export function PerSideControl({
  top,
  right,
  bottom,
  left,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = 'px',
  labels,
}: PerSideControlProps) {
  const { t } = useTranslation();
  const resolvedLabels = labels ?? {
    top: t('tenant.systemModuleBuilder.style.common.top'),
    right: t('tenant.systemModuleBuilder.style.common.right'),
    bottom: t('tenant.systemModuleBuilder.style.common.bottom'),
    left: t('tenant.systemModuleBuilder.style.common.left'),
  };
  const [linked, setLinked] = useState(
    top !== undefined && top === right && top === bottom && top === left,
  );

  const handleChange = (side: 'top' | 'right' | 'bottom' | 'left', val: number | undefined) => {
    if (linked) {
      onChange('top', val);
      onChange('right', val);
      onChange('bottom', val);
      onChange('left', val);
    } else {
      onChange(side, val);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setLinked(!linked)}
          className={cn(
            'gap-1 rounded px-1.5 py-0.5 text-[10px] font-normal',
            linked
              ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
          )}
        >
          {linked ? <Link className="w-3 h-3" /> : <Unlink className="w-3 h-3" />}
          {linked
            ? t('tenant.systemModuleBuilder.style.common.linked')
            : t('tenant.systemModuleBuilder.style.common.separate')}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <NumberInput
          value={top}
          onChange={(v) => handleChange('top', v)}
          min={min}
          max={max}
          step={step}
          unit={unit}
          placeholder={resolvedLabels.top}
          label={resolvedLabels.top}
          className="min-w-[7rem] flex-1"
        />
        <NumberInput
          value={right}
          onChange={(v) => handleChange('right', v)}
          min={min}
          max={max}
          step={step}
          unit={unit}
          placeholder={resolvedLabels.right}
          label={resolvedLabels.right}
          className="min-w-[7rem] flex-1"
        />
        <NumberInput
          value={bottom}
          onChange={(v) => handleChange('bottom', v)}
          min={min}
          max={max}
          step={step}
          unit={unit}
          placeholder={resolvedLabels.bottom}
          label={resolvedLabels.bottom}
          className="min-w-[7rem] flex-1"
        />
        <NumberInput
          value={left}
          onChange={(v) => handleChange('left', v)}
          min={min}
          max={max}
          step={step}
          unit={unit}
          placeholder={resolvedLabels.left}
          label={resolvedLabels.left}
          className="min-w-[7rem] flex-1"
        />
      </div>
    </div>
  );
}
